import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { Link, useParams } from 'react-router-dom';
import { ReactComponent as ChevronDown } from '../../../../assets/images/chevrondown.svg';
import { Accordion, AccordionItem as Item } from '@szhsin/react-accordion';
import { ReactComponent as EditSVG } from '../../../../assets/images/edit.svg';
import AdminModal from '../AdminModal';
import { useDashboardQuery } from '../../apis/dashboard';
import { useAssignUserToGroupMutation } from '../../apis/groups';
import { useParentDetailQuery } from '../../apis/parentDetail';
import {
  getParentGroupIds,
  getParentGroupNames,
  isSameId,
} from '../../apis/normalizeDashboardData';
import { hasFullFacilitatorProgramAccessRole } from '../../../../utils/authRole';
import ParentCertificate from '../../../course/Overview/ParentCertificate';
import ParentProgressReport from '../../../core/profile/ParentProgressReport';
import {
  modulePracticeDetails,
  moduleProgressDetailsTemplate,
} from './moduleProgressDetailsTemplate';

const S = {
  PageWrapper: styled.div`
    text-align: left;
    max-width: 1000px;
    margin: auto;
    white-space: normal;
  `,
};

const SURVEY_RATING_LABELS = {
  1: 'Very Helpful',
  2: 'Helpful',
  3: 'A Little Helpful',
  4: 'Not Helpful',
};

const SURVEY_RATING_COLORS = {
  'Very Helpful': '#019966',
  Helpful: '#fbaf43',
  'A Little Helpful': '#f05a2d',
  'Not Helpful': 'red',
};

const PARENT_DOCUMENT_BUTTON_CLASS_NAME =
  'inline-flex items-center justify-center rounded bg-purple px-4 py-2 text-sm font-bold text-white no-underline transition-colors duration-200 hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-70';

const legacyModuleItemIdsByTemplateId = {
  1: {
    11: 'check1-1',
    12: 'check1-2',
    13: 'badge11',
    14: 'check1-3',
    16: 'badge12',
  },
  2: {
    21: 'badge22',
    22: 'check2-1',
    23: 'badge23',
    24: 'check2-2',
    25: 'check2-3',
    26: 'badge24',
    27: 'check2-4',
    28: 'check2-5',
    29: 'check2-6',
    210: 'badge25',
  },
  3: {
    31: 'check3-1',
    32: 'check3-2',
    33: 'check3-3',
    34: 'badge32',
    35: 'check3-4',
    36: 'check3-5',
    37: 'badge33',
  },
  4: {
    41: 'badge42',
    42: 'check4-1',
    43: 'badge43',
    44: 'check4-2',
    45: 'wizard',
    46: 'check4-3',
    47: 'badge44',
    48: 'check4-4',
    49: 'check4-5',
    410: 'check4-6',
    411: 'check4-7',
    412: 'badge45',
  },
  5: {
    51: 'check5-1',
    52: 'check5-2',
    53: 'check5-3',
    54: 'check5-4',
    55: 'badge52',
    56: 'badge53',
    57: 'badge54',
  },
  6: {
    61: 'badge62',
    62: 'check6-1',
    63: 'badge63',
    64: 'check6-2',
    65: 'badge64',
    66: 'check6-3',
    67: 'check6-4',
    68: 'check6-5',
    69: 'badge65',
  },
};

const AccordionItem = ({
  header,
  buttonClassName = 'bg-white',
  panelClassName = 'p-4 bg-white',
  ...rest
}) => (
  <Item
    {...rest}
    header={({ state: { isEnter } }) => (
      <>
        {header}
        <div className='flex items-center'>
          <ChevronDown
            className={`ml-auto transition-transform duration-200 ease-out ${
              isEnter && 'rotate-180'
            } `}
          />
        </div>
      </>
    )}
    className='border-b'
    buttonProps={{
      className: ({ isEnter }) =>
        `flex w-full p-4 text-left ${buttonClassName} ${isEnter && ''}`,
    }}
    contentProps={{
      className: 'transition-height duration-200 ease-out',
    }}
    panelProps={{ className: panelClassName }}
  />
);

const getFullName = ({ firstName, lastName }) =>
  [firstName, lastName].filter(Boolean).join(' ').trim();

const formatDate = (date) => {
  if (!date) {
    return null;
  }

  return {
    day: format(parseISO(date), 'LL/dd/y'),
    time: format(parseISO(date), 'hh:mm aa'),
  };
};

const ItemDate = ({ date, className = '' }) => {
  const formattedDate = formatDate(date);

  if (!formattedDate) {
    return null;
  }

  return (
    <div className={`text-sm ${className}`.trim()}>
      <span className='font-bold'>{formattedDate.day}</span>{' '}
      <span className='text-sm font-semibold'>{formattedDate.time}</span>
    </div>
  );
};

const parsePracticeReview = (practiceReview) => {
  if (!practiceReview?.value) {
    return {};
  }

  try {
    const parsedValue = JSON.parse(practiceReview.value);

    return parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
  } catch {
    return {};
  }
};

const parsePracticeValue = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const formatPracticeValue = (value) => {
  const parsedValue = parsePracticeValue(value);

  if (Array.isArray(parsedValue)) {
    return parsedValue.join(', ');
  }

  if (typeof parsedValue === 'string') {
    return parsedValue.replace(/^"|"$/g, '');
  }

  return String(parsedValue ?? '');
};

const getSurveyRatingLabel = (rating) => {
  const numericRating = Number(rating);

  if (Number.isFinite(numericRating) && SURVEY_RATING_LABELS[numericRating]) {
    return SURVEY_RATING_LABELS[numericRating];
  }

  if (typeof rating === 'string') {
    return rating;
  }

  return String(rating ?? '');
};

const getSurveyRatingColor = (rating) => {
  const ratingLabel = getSurveyRatingLabel(rating);

  return SURVEY_RATING_COLORS[ratingLabel] || 'inherit';
};

const findPracticeValue = (practices, key) => {
  const practice = practices?.find((item) => item.name === key);
  return formatPracticeValue(practice?.value);
};

const buildPracticeTemplateParts = (text, practices = []) => {
  const parts = [];
  const pattern = /\{([^}]+)\}/g;
  let lastIndex = 0;
  let match = pattern.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      parts.push({
        text: text.slice(lastIndex, match.index),
        highlight: false,
      });
    }

    const responseText = findPracticeValue(practices, match[1]);

    if (responseText) {
      parts.push({
        text: responseText,
        highlight: true,
      });
    }

    lastIndex = match.index + match[0].length;
    match = pattern.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push({
      text: text.slice(lastIndex),
      highlight: false,
    });
  }

  return parts;
};

const fillPracticeTemplate = (text, practices = []) =>
  text.replace(/\{([^}]+)\}/g, (_, key) => findPracticeValue(practices, key));

const renderPracticeTextParts = (
  parts = [],
  highlightClassName = 'bg-[#fff9c4] px-1',
) =>
  parts.map((part, index) =>
    part.highlight ? (
      <span className={highlightClassName} key={`${part.text}-${index}`}>
        {part.text}
      </span>
    ) : (
      <React.Fragment key={`${part.text}-${index}`}>{part.text}</React.Fragment>
    ),
  );

const getPracticeReviewEntries = (moduleNumber, practiceReview, practices) => {
  const answers = parsePracticeReview(practiceReview);
  const questions =
    modulePracticeDetails[moduleNumber]?.review?.questions || [];

  if (!questions.length) {
    return Object.entries(answers).map(([key, value]) => ({
      key,
      label: key,
      labelParts: [{ text: key, highlight: false }],
      value: formatPracticeValue(value),
    }));
  }

  return questions.map((question) => ({
    key: question.key,
    label: fillPracticeTemplate(question.text, practices),
    labelParts: buildPracticeTemplateParts(question.text, practices),
    value: formatPracticeValue(answers[question.key]),
  }));
};

const getTemplateItemReferenceIds = (moduleNumber, item) => {
  const ids = new Set();

  if (item.id !== null && item.id !== undefined) {
    ids.add(String(item.id));
  }

  const legacyId =
    legacyModuleItemIdsByTemplateId[moduleNumber]?.[String(item.id)];

  if (legacyId) {
    ids.add(String(legacyId));
  }

  if (item.backendId !== null && item.backendId !== undefined) {
    ids.add(String(item.backendId));
  }

  if (item.type === 'survey_rating') {
    ids.add(`module-survey-${moduleNumber}`);
    ids.add('survey');
  }

  return [...ids];
};

const getDisplayReferenceId = (moduleNumber, item) => {
  if (item.type === 'survey_rating') {
    return `module-survey-${moduleNumber}`;
  }

  return (
    legacyModuleItemIdsByTemplateId[moduleNumber]?.[String(item.id)] ||
    String(item.id)
  );
};

const findProgressItemForTemplateItem = (
  moduleNumber,
  templateItem,
  progressItems = [],
) => {
  const referenceIds = getTemplateItemReferenceIds(moduleNumber, templateItem);

  return progressItems.find((progressItem) =>
    referenceIds.includes(String(progressItem.itemId)),
  );
};

const getModuleCompletedDate = (progressModule, templateItem, matchedItem) => {
  if (matchedItem?.dateCompleted) {
    return matchedItem.dateCompleted;
  }

  if (templateItem.name === 'Completion Badge') {
    return progressModule?.dateCompleted || null;
  }

  return null;
};

const hydrateModuleTemplateItem = (
  templateItem,
  moduleNumber,
  progressModule,
  practices = [],
) => {
  const matchedItem = findProgressItemForTemplateItem(
    moduleNumber,
    templateItem,
    progressModule?.items,
  );
  const dateCompleted = getModuleCompletedDate(
    progressModule,
    templateItem,
    matchedItem,
  );
  const referenceId = getDisplayReferenceId(moduleNumber, templateItem);

  if (templateItem.type === 'practice_assignment') {
    return {
      ...templateItem,
      referenceId,
      completed: Boolean(dateCompleted),
      dateCompleted,
      prompts: templateItem.prompts?.map((prompt) => ({
        text: fillPracticeTemplate(prompt.text, practices),
        parts: buildPracticeTemplateParts(prompt.text, practices),
      })),
    };
  }

  if (templateItem.type === 'practice_review') {
    const reviewDate = progressModule?.practiceReview?.date || null;

    return {
      ...templateItem,
      referenceId,
      completed: Boolean(reviewDate),
      dateCompleted: reviewDate,
      reviewEntries: getPracticeReviewEntries(
        moduleNumber,
        progressModule?.practiceReview,
        practices,
      ),
    };
  }

  if (templateItem.type === 'survey_rating') {
    return {
      ...templateItem,
      referenceId,
      completed: Boolean(dateCompleted),
      dateCompleted,
      rating: matchedItem?.rating ?? null,
      ratingLabel:
        matchedItem?.rating !== null && matchedItem?.rating !== undefined
          ? getSurveyRatingLabel(matchedItem.rating)
          : null,
    };
  }

  return {
    ...templateItem,
    referenceId,
    completed: Boolean(dateCompleted),
    dateCompleted,
  };
};

const flattenSectionItems = (sections = []) =>
  sections.flatMap((section) => section.items);

const getProgressModules = (parentDetail) =>
  parentDetail?.modules || parentDetail?.module || [];

const getCurrentProgressModuleNumber = (parentDetail) => {
  const rawModuleIndex = Number(parentDetail?.progress?.modindex);

  if (!Number.isFinite(rawModuleIndex) || rawModuleIndex < 0) {
    return null;
  }

  return rawModuleIndex + 1;
};

const hasInProgressEngagementScore = (module) =>
  !module?.completed &&
  module?.engagementScore !== null &&
  module?.engagementScore !== undefined;

const buildDisplayModules = (parentDetail) => {
  const progressModulesByNumber = new Map(
    getProgressModules(parentDetail).map((module) => [
      Number(module.moduleNumber),
      module,
    ]),
  );
  const practices = parentDetail?.practices || [];
  const currentProgressModuleNumber =
    getCurrentProgressModuleNumber(parentDetail);

  const rawModules = moduleProgressDetailsTemplate.map((templateModule) => {
    const progressModule = progressModulesByNumber.get(templateModule.module);
    const sections = templateModule.items.map((section) => ({
      ...section,
      items: section.items.map((item) =>
        hydrateModuleTemplateItem(
          item,
          templateModule.module,
          progressModule,
          practices,
        ),
      ),
    }));
    const completionBadgeItem = flattenSectionItems(sections).find(
      (item) => item.name === 'Completion Badge',
    );

    return {
      moduleNumber: templateModule.module,
      moduleName: templateModule.moduleName,
      engagementScore: progressModule?.engagementScore,
      sections,
      completed: Boolean(progressModule?.dateCompleted),
      dateCompleted:
        progressModule?.dateCompleted ||
        completionBadgeItem?.dateCompleted ||
        null,
    };
  });
  const engagementCurrentModuleNumber =
    rawModules.find((module) => hasInProgressEngagementScore(module))
      ?.moduleNumber ?? null;
  const derivedCurrentModuleNumber =
    engagementCurrentModuleNumber ?? currentProgressModuleNumber;

  if (derivedCurrentModuleNumber !== null) {
    return rawModules.map((module) => {
      if (module.completed) {
        return {
          ...module,
          status: 'completed',
        };
      }

      if (module.moduleNumber === derivedCurrentModuleNumber) {
        return {
          ...module,
          status: 'current',
        };
      }

      if (module.moduleNumber < derivedCurrentModuleNumber) {
        return {
          ...module,
          status: 'available',
        };
      }

      return {
        ...module,
        status: 'locked',
      };
    });
  }

  let currentModuleAssigned = false;

  return rawModules.map((module, index) => {
    const previousModulesCompleted =
      index === 0
        ? true
        : rawModules
            .slice(0, index)
            .every((previousModule) => previousModule.completed);

    if (module.completed) {
      return {
        ...module,
        status: 'completed',
      };
    }

    if (!currentModuleAssigned && previousModulesCompleted) {
      currentModuleAssigned = true;

      return {
        ...module,
        status: 'current',
      };
    }

    return {
      ...module,
      status: 'locked',
    };
  });
};

const getModuleButtonClassName = (status) => {
  if (status === 'current') {
    return 'border-l-4 border-sky-200 bg-white';
  }

  if (status === 'locked') {
    return 'bg-gray-300 text-gray-600';
  }

  return 'bg-white';
};

const getModulePanelClassName = (status) => {
  if (status === 'current') {
    return 'border-l-4 border-sky-100 bg-white p-4';
  }

  if (status === 'locked') {
    return 'p-4 bg-gray-50';
  }

  return 'p-4 bg-white';
};

const isPracticeAssignmentSection = (section) =>
  section.sectionName === 'Practice Assignment';

const isPracticeReviewSection = (section) =>
  section.sectionName === 'Practice Review';

const EditGroupModal = ({
  user,
  groups,
  isOpen,
  handleClose,
  isGroupsLoading,
  isGroupsError,
}) => {
  const [selectedGroup, setSelectedGroup] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const assignUserToGroupMutation = useAssignUserToGroupMutation({
    onSuccess: () => {
      setErrorMessage('');
      handleClose();
    },
  });
  const canCreateGroup = hasFullFacilitatorProgramAccessRole(
    localStorage.getItem('role'),
  );

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    setSelectedGroup(user.groupIds?.length === 1 ? user.groupIds[0] : '');
    setErrorMessage('');
  }, [groups, user]);

  if (!user) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedGroup) {
      setErrorMessage('Select a group before saving changes.');
      return;
    }

    try {
      await assignUserToGroupMutation.mutateAsync({
        groupId: selectedGroup,
        userId: user.id,
      });
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to update the parent group.');
    }
  };

  return (
    <AdminModal
      title={
        <>
          Edit Groups for{' '}
          <span className='text-purple underline'>{user.name}</span>
        </>
      }
      isOpen={isOpen}
      handleClose={() => {
        setErrorMessage('');
        handleClose();
      }}
    >
      {isGroupsLoading ? (
        <p className='text-sm text-gray-700'>Loading groups...</p>
      ) : isGroupsError ? (
        <p className='text-sm font-bold text-red-700'>
          Unable to load groups right now.
        </p>
      ) : groups.length === 0 ? (
        <div className='flex flex-col gap-4'>
          <p className='max-w-[46ch] text-sm leading-relaxed text-gray-700'>
            {canCreateGroup
              ? 'No groups have been created yet. Create a group first, then you can assign this participant to it.'
              : 'No groups have been created yet. An administrator will need to create a group before this participant can be assigned to one.'}
          </p>
          {canCreateGroup ? (
            <Link
              to='/facilitator/dashboard/create-group'
              onClick={handleClose}
              className='inline-flex w-fit items-center justify-center rounded bg-purple px-4 py-2 text-sm font-bold text-white no-underline transition-colors duration-200 hover:bg-purple-800'
            >
              Create A New Group
            </Link>
          ) : null}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className='flex flex-col gap-4'>
            {errorMessage ? (
              <p className='rounded border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700'>
                {errorMessage}
              </p>
            ) : null}
            {groups.map((group) => (
              <label
                key={group.groupId}
                className='flex items-start gap-3 rounded border border-gray-200 bg-white p-3 text-sm'
              >
                <input
                  type='radio'
                  name='group-selection'
                  value={group.groupId}
                  checked={selectedGroup === group.groupId}
                  onChange={() => {
                    setSelectedGroup(group.groupId);
                    setErrorMessage('');
                  }}
                  className='mt-1 accent-blue-600'
                />
                <span>
                  <span className='block font-bold text-dark-blue'>
                    {group.groupName}
                  </span>
                  <span className='block text-xs text-gray-700'>
                    Parents:{' '}
                    <span className='font-bold'>{group.parentCount}</span>
                  </span>
                  <span className='block text-xs text-gray-700'>
                    Facilitators:{' '}
                    <span className='font-bold'>
                      {group.facilitators?.length
                        ? group.facilitators
                            .map((facilitator) => getFullName(facilitator))
                            .filter(
                              (name) =>
                                name && name.toLowerCase() !== 'unassigned',
                            )
                            .join(', ')
                        : 'None assigned'}
                    </span>
                  </span>
                </span>
              </label>
            ))}
            <button
              type='submit'
              className='mt-2 rounded bg-purple px-4 py-2 text-sm font-bold text-white transition-colors duration-200 hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-70'
              disabled={assignUserToGroupMutation.isLoading}
            >
              {assignUserToGroupMutation.isLoading
                ? 'Saving...'
                : 'Save Changes'}
            </button>
          </div>
        </form>
      )}
    </AdminModal>
  );
};

const UserDetailPage = () => {
  const { id } = useParams();
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);
  const canEditGroups = hasFullFacilitatorProgramAccessRole(
    localStorage.getItem('role'),
  );
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
  } = useDashboardQuery();

  const { data: parentDetail, error, isPending } = useParentDetailQuery(id);

  const parentName = useMemo(
    () => getFullName(parentDetail?.parentInfo || {}),
    [parentDetail],
  );

  const dashboardParent = useMemo(
    () =>
      dashboardData?.parents.find((parent) => isSameId(parent.userId, id)) ??
      null,
    [dashboardData, id],
  );

  const currentGroupNames = useMemo(() => {
    if (dashboardParent) {
      return getParentGroupNames(dashboardParent);
    }

    return getParentGroupNames(parentDetail);
  }, [dashboardParent, parentDetail]);

  const modalUser = useMemo(() => {
    if (!parentDetail) {
      return null;
    }

    return {
      id: parentDetail.parentInfo.userId,
      name: parentName,
      email: parentDetail.parentInfo.email,
      phone: parentDetail.parentInfo.phone,
      groupIds: dashboardParent
        ? getParentGroupIds(dashboardParent)
        : getParentGroupIds(parentDetail),
    };
  }, [dashboardParent, parentDetail, parentName]);

  const displayModules = useMemo(
    () => buildDisplayModules(parentDetail),
    [parentDetail],
  );

  const programCompletionDate = useMemo(() => {
    const hasCompletedProgram =
      displayModules.length === 6 &&
      displayModules.every((module) => module.completed);

    if (!hasCompletedProgram) {
      return null;
    }

    return (
      displayModules.find((module) => module.moduleNumber === 6)
        ?.dateCompleted || null
    );
  }, [displayModules]);

  const certificateFacilitators = useMemo(() => {
    const detailGroupIds = getParentGroupIds(parentDetail);
    const parentGroupIds = detailGroupIds.length
      ? detailGroupIds
      : getParentGroupIds(dashboardParent);

    return (dashboardData?.groups ?? [])
      .filter((group) =>
        parentGroupIds.some((groupId) => isSameId(groupId, group.groupId)),
      )
      .flatMap((group) => group.facilitators ?? []);
  }, [dashboardData, dashboardParent, parentDetail]);

  if (isPending) {
    return <S.PageWrapper>Loading parent detail...</S.PageWrapper>;
  }

  if (error || !parentDetail) {
    return <S.PageWrapper>Unable to load parent detail.</S.PageWrapper>;
  }

  return (
    <S.PageWrapper>
      {canEditGroups ? (
        <EditGroupModal
          user={modalUser}
          groups={dashboardData?.groups ?? []}
          isOpen={isEditGroupOpen}
          handleClose={() => setIsEditGroupOpen(false)}
          isGroupsLoading={isDashboardLoading}
          isGroupsError={isDashboardError}
        />
      ) : null}
      <div className='my-3'>
        <Link
          className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
          to='/facilitator/dashboard'
        >
          &larr; Go Back
        </Link>
      </div>
      <div className='my-8 overflow-hidden rounded-lg border border-gray-200 bg-slate-50 shadow-sm'>
        <div className='flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-blue-50 px-4 py-4'>
          <div className='flex flex-col gap-1 border-l-4 border-purple pl-3'>
            <h1 className='text-2xl font-bold text-dark-blue'>{parentName}</h1>
            <p className='text-sm font-bold uppercase tracking-wide text-purple'>
              Parent Profile
            </p>
          </div>
        </div>
        <div className='grid gap-3 bg-white px-4 py-4 text-sm sm:grid-cols-2'>
          <p className='rounded border border-gray-200 bg-slate-50 px-3 py-2 font-bold text-gray-700'>
            <span className='text-dark-blue'>Name:</span>{' '}
            <span className='text-gray-800'>{parentName}</span>
          </p>
          <p className='rounded border border-gray-200 bg-slate-50 px-3 py-2 font-bold text-gray-700'>
            <span className='text-dark-blue'>Username:</span>{' '}
            <span className='text-gray-800'>
              {parentDetail.parentInfo.username || 'N/A'}
            </span>
          </p>
          <p className='rounded border border-gray-200 bg-slate-50 px-3 py-2 font-bold text-gray-700'>
            <span className='text-dark-blue'>Email:</span>{' '}
            <a
              className='text-blue-500 hover:underline'
              href={`mailto:${parentDetail.parentInfo.email}`}
            >
              {parentDetail.parentInfo.email}
            </a>
          </p>
          <p className='rounded border border-gray-200 bg-slate-50 px-3 py-2 font-bold text-gray-700'>
            <span className='text-dark-blue'>Phone:</span>{' '}
            <span className='text-gray-800'>
              {parentDetail.parentInfo.phone}
            </span>
          </p>
          <div className='flex flex-wrap items-center gap-2 pt-1 sm:col-span-2'>
            {programCompletionDate ? (
              <ParentCertificate
                compact
                parentName={parentName}
                completedAt={programCompletionDate}
                facilitatorNames={certificateFacilitators}
                className={PARENT_DOCUMENT_BUTTON_CLASS_NAME}
              />
            ) : null}
            <ParentProgressReport
              completions={parentDetail.modules}
              parentName={parentName}
              className={PARENT_DOCUMENT_BUTTON_CLASS_NAME}
            />
          </div>
        </div>
      </div>

      <section className='my-6 rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
        <div className='mb-2 text-xs font-bold uppercase tracking-wide text-purple'>
          Group Assignment
        </div>
        <div className='flex flex-wrap items-start justify-between gap-4'>
          <div className='text-sm'>
            <div className='mb-1'>
              <span className='font-bold text-dark-blue'>Current Group:</span>{' '}
              {currentGroupNames.length ? (
                <span className='text-gray-800'>
                  {currentGroupNames.join(', ')}
                </span>
              ) : (
                <span className='font-bold text-red-600'>Group Unassigned</span>
              )}
            </div>
            <div className='text-gray-700'>
              {canEditGroups
                ? "Update this parent's group assignment here."
                : 'Group assignments are read only for facilitators.'}
            </div>
          </div>
          {canEditGroups ? (
            <button
              type='button'
              onClick={() => setIsEditGroupOpen(true)}
              className='inline-flex min-h-[38px] items-center gap-2 rounded bg-light-blue px-3 py-1.5 text-sm font-bold text-white no-underline transition-colors duration-200 hover:bg-blue-600'
            >
              <EditSVG className='w-4 fill-current' />
              Edit Group
            </button>
          ) : null}
        </div>
      </section>

      <section className='my-6 rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
        <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
          <h2 className='border-l-4 border-purple pl-3 text-xl font-bold text-dark-blue'>
            Module Progress Details
          </h2>
        </div>
        <Accordion
          className='overflow-hidden rounded border border-gray-200 bg-white'
          transition
          transitionTimeout={200}
        >
          <div>
            {displayModules.map((module) => (
              <AccordionItem
                key={module.moduleNumber}
                buttonClassName={getModuleButtonClassName(module.status)}
                panelClassName={getModulePanelClassName(module.status)}
                header={
                  <div className='flex w-full items-center justify-between gap-4'>
                    <div className='flex items-center gap-3'>
                      <div className='flex h-6 w-6 items-center justify-center'>
                        {module.status === 'completed' ? (
                          <span className='text-lg font-bold leading-none text-green-600'>
                            ✓
                          </span>
                        ) : module.status === 'current' ? (
                          <span className='block h-4 w-4 rounded-full border-2 border-sky-500 bg-[#dff7ff]' />
                        ) : module.status === 'available' ? (
                          <span className='block h-4 w-4 rounded-full border border-gray-400 bg-white' />
                        ) : (
                          <span className='block h-3.5 w-3.5 rounded-full border border-gray-400 bg-gray-300' />
                        )}
                      </div>
                      <div className='flex flex-col items-start gap-1'>
                        <h3
                          className={`text-lg ${
                            module.status === 'locked' ? 'text-gray-500' : ''
                          }`}
                        >
                          <span className='font-bold'>
                            Module {module.moduleNumber}:{' '}
                          </span>
                          {module.moduleName}
                        </h3>
                        {module.dateCompleted ? (
                          <p
                            className={`text-sm font-medium ${
                              module.status === 'locked'
                                ? 'text-gray-500'
                                : 'text-gray-700'
                            }`}
                          >
                            Completed On:{' '}
                            {formatDate(module.dateCompleted)?.day}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div
                      className={`mr-2 rounded-lg px-3 py-2 text-sm font-bold ${
                        module.status === 'locked'
                          ? 'bg-white/70 text-gray-500'
                          : module.status === 'current'
                            ? 'border border-sky-100 bg-[#f7fdff] text-gray-700'
                            : 'bg-white text-gray-700'
                      }`}
                    >
                      {module.status === 'locked' ? (
                        <span>Locked</span>
                      ) : module.completed &&
                        module.engagementScore !== null &&
                        module.engagementScore !== undefined ? (
                        <>
                          <span>Engagement Index: </span>
                          <span
                            className={
                              module.engagementScore >= 60
                                ? 'text-green'
                                : 'text-red-600'
                            }
                          >
                            {module.engagementScore}%
                          </span>
                        </>
                      ) : module.status === 'current' ? (
                        <span>Current Module</span>
                      ) : module.status === 'available' ? (
                        <span>Not Completed</span>
                      ) : (
                        <span>Completed</span>
                      )}
                    </div>
                  </div>
                }
              >
                <div className='flex flex-col'>
                  {module.sections.map(
                    (section, sectionIndex, sectionArray) => {
                      const moduleLocked = module.status === 'locked';
                      const showIncompleteIcon = Boolean(module.dateCompleted);

                      return (
                        <div
                          key={section.sectionName}
                          className={`pb-4 border-l-2 border-dashed ${
                            sectionIndex + 1 === sectionArray.length
                              ? 'border-transparent'
                              : moduleLocked
                                ? 'border-gray-300'
                                : 'border-gray-500'
                          }`}
                        >
                          <h4
                            className={`text-lg relative font-bold before:content-[''] before:absolute before:left-[-1.55rem] before:top-1/2 before:-translate-y-1/2 before:w-4 before:h-4 before:rounded-full ml-4 leading-4 mb-2 ${
                              moduleLocked
                                ? 'text-gray-500 before:bg-gray-300'
                                : 'text-blue-800 before:bg-gray-500'
                            }`}
                          >
                            {section.sectionName}
                          </h4>

                          {isPracticeAssignmentSection(section) ? (
                            <div
                              className={`ml-4 mt-4 space-y-3 text-sm ${
                                moduleLocked ? 'text-gray-500' : 'text-gray-800'
                              }`}
                              data-item-id={section.items[0]?.referenceId}
                            >
                              {section.items[0]?.prompts?.map(
                                (prompt, index) => (
                                  <div
                                    className='flex items-start gap-2'
                                    key={index}
                                  >
                                    <div
                                      className={`mt-1 h-2.5 w-2.5 rounded-full ${
                                        moduleLocked
                                          ? 'bg-gray-300'
                                          : 'bg-[#eefbff]'
                                      }`}
                                    />
                                    <p>
                                      {renderPracticeTextParts(
                                        prompt.parts ?? [
                                          {
                                            text:
                                              prompt.text ??
                                              String(prompt ?? ''),
                                            highlight: false,
                                          },
                                        ],
                                      )}
                                    </p>
                                  </div>
                                ),
                              )}
                            </div>
                          ) : isPracticeReviewSection(section) ? (
                            <div
                              className='ml-4 mt-4'
                              data-item-id={section.items[0]?.referenceId}
                            >
                              <ItemDate
                                date={section.items[0]?.dateCompleted}
                                className={`ml-5 ${
                                  moduleLocked ? 'text-gray-500' : 'text-black'
                                }`}
                              />
                              <div
                                className={`space-y-3 text-sm ${
                                  moduleLocked
                                    ? 'mt-0 text-gray-500'
                                    : 'mt-4 text-gray-800'
                                }`}
                              >
                                {section.items[0]?.reviewEntries?.map(
                                  (entry) => (
                                    <div className='ml-4' key={entry.key}>
                                      <p
                                        className={`font-semibold ${
                                          moduleLocked
                                            ? 'text-gray-500'
                                            : 'text-dark-blue'
                                        }`}
                                      >
                                        {renderPracticeTextParts(
                                          entry.labelParts ?? [
                                            {
                                              text: entry.label ?? '',
                                              highlight: false,
                                            },
                                          ],
                                        )}
                                      </p>
                                      <p className='mt-1'>
                                        {entry.value ? (
                                          <span
                                            className={
                                              moduleLocked
                                                ? 'text-gray-500'
                                                : 'bg-[#fff9c4] px-1'
                                            }
                                          >
                                            {entry.value}
                                          </span>
                                        ) : moduleLocked ? (
                                          <span className='text-gray-400'>
                                            Locked
                                          </span>
                                        ) : (
                                          <span className='font-semibold text-red-600'>
                                            Not Answered
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          ) : (
                            <ul className='text-base'>
                              {section.items
                                .filter(
                                  (item) => item.type !== 'practice_assignment',
                                )
                                .map((item) => (
                                  <li className='mb-2' key={item.referenceId}>
                                    <div
                                      className={`flex items-center gap-2 ml-4 ${
                                        moduleLocked ? 'text-gray-500' : ''
                                      }`}
                                      data-item-id={item.referenceId}
                                    >
                                      <div
                                        className={`w-3 h-3 border-2 border-solid rounded-full ${
                                          moduleLocked
                                            ? 'border-gray-400 bg-gray-200'
                                            : 'border-sky-300 bg-[#eefbff]'
                                        }`}
                                      />
                                      <div className='flex items-center gap-2'>
                                        <span>{item.name}</span>
                                        {item.completed ? (
                                          <span>✅</span>
                                        ) : showIncompleteIcon ? (
                                          <span>❌</span>
                                        ) : null}
                                      </div>
                                    </div>
                                    <ItemDate
                                      date={item.dateCompleted}
                                      className={`ml-5 ${
                                        moduleLocked
                                          ? 'text-gray-500'
                                          : 'text-black'
                                      }`}
                                    />
                                    {item.type === 'survey_rating' &&
                                    item.rating !== null &&
                                    item.rating !== undefined ? (
                                      <div
                                        className={`ml-5 mt-1 text-sm ${
                                          moduleLocked
                                            ? 'text-gray-500'
                                            : 'text-gray-700'
                                        }`}
                                      >
                                        <span className='font-semibold'>
                                          Rating:
                                        </span>{' '}
                                        <span
                                          className='font-semibold'
                                          style={{
                                            color: moduleLocked
                                              ? undefined
                                              : getSurveyRatingColor(
                                                  item.rating,
                                                ),
                                          }}
                                        >
                                          {item.ratingLabel ||
                                            getSurveyRatingLabel(item.rating)}
                                        </span>
                                      </div>
                                    ) : null}
                                  </li>
                                ))}
                            </ul>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              </AccordionItem>
            ))}
          </div>
        </Accordion>
      </section>
    </S.PageWrapper>
  );
};

export default UserDetailPage;
